import * as React from "react";
import {Fragment, useEffect, useMemo, useState} from "react";
import {toast} from "react-toastify";
import {useAxiosPrivate} from "@/hooks/useAxiosPrivate";
import useDebounce from "@/hooks/useDebounce";
// NOTE: Add `sendBulkCommunicationApi` to your userApi service file.
// It should POST to /admin/users/bulk-communicate with { userIds, channels, subject, messageBody }
import {
	exportBulkUsers,
	getDistinctFamilyTreeNamesApi,
	getPaginatedUsers,
	sendBulkCommunicationApi,
	sendBulkWhatsAppApi,
} from "@/service/admin/userApi";

import {
	AdjustmentsHorizontalIcon,
	ArrowDownTrayIcon,
	ArrowPathIcon,
	ChatBubbleLeftEllipsisIcon,
	ChevronDoubleLeftIcon,
	ChevronDoubleRightIcon,
	ChevronDownIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	ChevronUpIcon,
	EllipsisHorizontalIcon,
	EnvelopeIcon,
	ExclamationTriangleIcon,
	FunnelIcon,
	MagnifyingGlassIcon,
	XMarkIcon,
} from "@heroicons/react/24/outline";
import {Dialog, Menu, Popover, Transition} from "@headlessui/react";
import {flexRender, getCoreRowModel, useReactTable} from "@tanstack/react-table";
import {ClipboardDocumentIcon} from "@heroicons/react/16/solid";
import {FlyoutSuperUserNavigationMenu} from "@/components/admin/FlyoutSuperUserNavigationMenu";
import {FaWhatsapp} from "react-icons/fa";

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS & MICRO-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derives account status from user data:
 * - active:       registeredAt set AND email verified
 * - pending:      has an email but registration incomplete / email unverified
 * - unregistered: no email at all (family-tree-only node)
 */
const getAccountStatus = (user) => {
	if (user.registeredAt && user.verified_email) return "active";
	if (user.email) return "pending";
	return "unregistered";
};

const STATUS_STYLES = {
	active: "bg-emerald-50 text-emerald-700 border-emerald-200",
	pending: "bg-amber-50  text-amber-700  border-amber-200",
	unregistered: "bg-gray-100  text-gray-500   border-gray-200",
};
const STATUS_LABELS = {active: "Active", pending: "Pending", unregistered: "Unregistered"};

const BLANK_AWARE_OPTIONS = [
	{label: "Has a value", value: "__not_blank__"},
	{label: "Is blank", value: "__blank__"},
];

/** Per-column filter configuration. Only columns listed here get a filter icon. */
const COLUMN_FILTER_CONFIGS = {
	"phone.number": {options: BLANK_AWARE_OPTIONS},
	email: {options: BLANK_AWARE_OPTIONS},
	membershipNumber: {options: BLANK_AWARE_OPTIONS},
	familyTreeName: {options: BLANK_AWARE_OPTIONS},
	gender: {
		options: [
			...BLANK_AWARE_OPTIONS,
			{label: "Male", value: "Male"},
			{label: "Female", value: "Female"},
			{label: "Other", value: "Other"},
		],
	},
	isMember: {
		options: [
			{label: "Member", value: "true"},
			{label: "Non-Member", value: "false"},
		],
	},
	membershipType: {
		options: [
			...BLANK_AWARE_OPTIONS,
			{label: "Life Member", value: "Life Member"},
			{label: "Annual Member", value: "Annual Member"},
			{label: "Honorary Member", value: "Honorary Member"},
		],
	},
	generation: {
		options: [
			...BLANK_AWARE_OPTIONS,
			...Array.from({length: 12}, (_, i) => ({
				label: `Gen ${i + 1}`, value: String(i + 1)
			})),
		],
	},
	dateOfBirth: {options: BLANK_AWARE_OPTIONS},
	maritalStatus: {
		options: [
			...BLANK_AWARE_OPTIONS,
			{label: "Single", value: "Single"},
			{label: "Married", value: "Married"},
			{label: "Minor", value: "Minor"},
			{label: "Widower", value: "Widower"},
			{label: "Widowed", value: "Widowed"},
			{label: "Divorced", value: "Divorced"},
		],
	},
	bloodGroup: {
		options: [
			...BLANK_AWARE_OPTIONS,
			...["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"].map(g => ({label: g, value: g})),
		],
	},
	"address.addressLine1": {options: BLANK_AWARE_OPTIONS},
	"address.city": {options: BLANK_AWARE_OPTIONS},
	"address.state": {options: BLANK_AWARE_OPTIONS},
	"address.country": {options: BLANK_AWARE_OPTIONS},
	deceased: {
		options: [
			{label: "Deceased", value: "true"},
			{label: "Living", value: "false"},
		],
	},
	outsider: {
		options: [
			{label: "Outsider (Non-Joglekar)", value: "true"},
			{label: "Joglekar", value: "false"},
		],
	},
	sideOfFamily: {
		options: [
			...BLANK_AWARE_OPTIONS,
			{label: "MaherVashin", value: "MaherVashin"},
		],
	},
	pageNumber: {options: BLANK_AWARE_OPTIONS},
	emailVerified: {
		options: [
			{label: "Verified", value: "true"},
			{label: "Not verified", value: "false"},
		],
	},
	phoneNumberWhatsAppVerified: {
		options: [
			{label: "Verified", value: "true"},
			{label: "Not verified", value: "false"},
		],
	},
};

/** Small popover that attaches to a column header for per-column filtering. */
const ColumnFilterPopover = ({columnId, currentValue, onFilterChange, configOptions}) => {
	const config = configOptions ?? COLUMN_FILTER_CONFIGS[columnId];

	// Use Set internally to prevent duplicates and get O(1) lookup
	const [pendingSet, setPendingSet] = useState(
		() => new Set(Array.isArray(currentValue) ? currentValue : [])
	);

	// No useEffect needed — use key prop on call site to remount when currentValue changes

	if (!config) return null;

	const isActive = Array.isArray(currentValue) && currentValue.length > 0;

	const toggleOption = (value) => {
		setPendingSet(prev => {
			const next = new Set(prev);
			if (next.has(value)) next.delete(value);
			else next.add(value);
			return next;
		});
	};

	const pendingArray = Array.from(pendingSet);

	return (
		<Popover className="relative inline-flex">
			{({open}) => (
				<>
					<Popover.Button
						className={`p-0.5 rounded transition-colors outline-none
                            ${isActive
							? "text-[#D26A53]"
							: open
								? "text-gray-700 bg-gray-100"
								: "text-gray-400 hover:text-gray-600"}`}
						onClick={(e) => e.stopPropagation()}
					>
						<FunnelIcon className={`h-3.5 w-3.5 ${isActive ? "fill-[#D26A53]/20" : ""}`}/>
					</Popover.Button>

					<Transition
						as={Fragment}
						enter="transition duration-100 ease-out"
						enterFrom="transform scale-95 opacity-0"
						enterTo="transform scale-100 opacity-100"
						leave="transition duration-75 ease-out"
						leaveFrom="transform scale-100 opacity-100"
						leaveTo="transform scale-95 opacity-0"
					>
						<Popover.Panel
							className="absolute top-full left-0 z-20 mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-xl"
							onClick={(e) => e.stopPropagation()}
						>
							{({close}) => (
								<div className="flex flex-col">
									{/* Header */}
									<div
										className="px-3 pt-2.5 pb-1.5 border-b border-gray-100 flex items-center justify-between">
										<p className="text-[10px] font-custom-semi-bold text-gray-400 uppercase tracking-wider">
											Filter
										</p>
										{pendingSet.size > 0 && (
											<button
												onClick={() => setPendingSet(new Set())}
												className="text-[10px] text-[#D26A53] font-custom-semi-bold hover:underline"
											>
												Clear
											</button>
										)}
									</div>

									{/* Options */}
									<div className="flex flex-col max-h-52 overflow-y-auto py-1 thin-scrollbar">
										{config.options.map((opt) => {
											const checked = pendingSet.has(opt.value);
											const isBlankOpt = opt.value === "__blank__" || opt.value === "__not_blank__";
											return (
												<label
													key={opt.value}
													className={`flex items-center gap-2.5 px-3 py-1.5 cursor-pointer
                                                        hover:bg-gray-50 transition-colors select-none
                                                        ${checked ? "bg-[#D26A53]/5" : ""}
                                                        ${isBlankOpt ? "text-gray-400 italic" : "text-gray-700"}`}
												>
													<input
														type="checkbox"
														checked={checked}
														onChange={() => toggleOption(opt.value)}
														className="rounded border-gray-300 text-[#D26A53]
                                                            focus:ring-[#D26A53] h-3.5 w-3.5 shrink-0 cursor-pointer"
													/>
													<span className="text-xs font-custom-medium truncate">
                                                        {opt.label}
                                                    </span>
												</label>
											);
										})}
									</div>

									{/* Footer */}
									<div className="flex gap-2 p-2 border-t border-gray-100">
										<button
											onClick={() => {
												// Discard changes — reset pending to current applied value
												setPendingSet(new Set(Array.isArray(currentValue) ? currentValue : []));
												close();
											}}
											className="flex-1 px-2 py-1.5 text-xs font-custom-semi-bold
                                                text-gray-600 border border-gray-200 rounded-lg
                                                hover:bg-gray-50 transition-colors"
										>
											Cancel
										</button>
										<button
											onClick={() => {
												onFilterChange(columnId, pendingArray.length > 0 ? pendingArray : undefined);
												close();
											}}
											className="flex-1 px-2 py-1.5 text-xs font-custom-semi-bold
                                                text-white bg-[#D26A53] hover:bg-[#b85b46]
                                                rounded-lg transition-colors flex items-center justify-center gap-1"
										>
											Apply
											{pendingSet.size > 0 && (
												<span className="bg-white/25 rounded px-1 text-[10px]">
                                                    {pendingSet.size}
                                                </span>
											)}
										</button>
									</div>
								</div>
							)}
						</Popover.Panel>
					</Transition>
				</>
			)}
		</Popover>
	);
};

// Custom
const ACCOUNT_STATUS_TABS = [
	{id: "all", label: "All Members"},
	{id: "registered", label: "Verified"},
	{id: "pending", label: "Registered"},
	{id: "unregistered", label: "Unregistered"},
	{id: "loggedin", label: "Logged In"},
];

// Human-readable labels used in filter chips
const COLUMN_LABELS = {
	email: "Email", gender: "Gender", "phone.number": "Phone",
	membershipNumber: "Membership No.", isMember: "Membership",
	membershipType: "Type", generation: "Generation", familyTreeName: "Family Tree",
	dateOfBirth: "DOB", maritalStatus: "Marital", bloodGroup: "Blood",
	"address.addressLine1": "Address", "address.city": "City",
	"address.state": "State", "address.country": "Country",
	deceased: "Deceased", outsider: "Outsider",
	sideOfFamily: "Family Side", pageNumber: "Page No.",
	emailVerified: "Email", phoneNumberWhatsAppVerified: "WhatsApp"
};

// Maps TanStack column ID → Excel { header, key } config
// Only columns that make sense in an export are listed here
// (select, _id, actions are intentionally excluded)
const COLUMN_EXPORT_CONFIG = {
	fullName: {header: "Full Name", key: "fullName"},
	"phone.number": {header: "Phone", key: "phone"},
	email: {header: "Email", key: "email"},
	membershipNumber: {header: "Membership No.", key: "membershipNumber"},
	isMember: {header: "Membership", key: "isMember"},
	gender: {header: "Gender", key: "gender"},
	membershipType: {header: "Membership Type", key: "membershipType"},
	generation: {header: "Generation", key: "generation"},
	familyTreeName: {header: "Family Tree", key: "familyTreeName"},
	registeredAt: {header: "Registered On", key: "registeredAt"},
	dateOfBirth: {header: "Date of Birth", key: "dateOfBirth"},
	maritalStatus: {header: "Marital Status", key: "maritalStatus"},
	bloodGroup: {header: "Blood Group", key: "bloodGroup"},
	"address.addressLine1": {header: "Address Line 1", key: "addressLine1"},
	"address.city": {header: "City", key: "city"},
	"address.state": {header: "State", key: "state"},
	"address.country": {header: "Country", key: "country"},
	deceased: {header: "Deceased", key: "deceased"},
	outsider: {header: "Outsider", key: "outsider"},
	sideOfFamily: {header: "Side of Family", key: "sideOfFamily"},
	pageNumber: {header: "Page No.", key: "pageNumber"},
	emailVerified: {header: "Email Verified", key: "emailVerified"},
	phoneNumberWhatsAppVerified: {header: "WA Verified", key: "phoneNumberWhatsAppVerified"},
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export const Users = (props) => {
	const {selectedMenuOption, setSelectedMenuOption} = props;
	const axiosPrivate = useAxiosPrivate();

	// ── 1. STATE ──────────────────────────────────────────────────────────────

	const [usersData, setUsersData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [pageCount, setPageCount] = useState(0);
	const [totalUsers, setTotalUsers] = useState(0);

	// Global search (debounced)
	const [globalSearch, setGlobalSearch] = useState("");
	const debouncedSearch = useDebounce(globalSearch, 500);

	// Advanced filters
	const [columnFilters, setColumnFilters] = useState({}); // Values are now arrays: { gender: ["Male"], isMember: ["true"], "phone.number": ["__blank__"] }
	const [accountStatusFilter, setAccountStatusFilter] = useState("all"); // "all" | "registered" | "pending" | "unregistered"

	// TanStack Table state
	const [rowSelection, setRowSelection] = useState({});
	const [sorting, setSorting] = useState([{id: "createdAt", desc: true}]);
	const [columnVisibility, setColumnVisibility] = useState({
		gender: false,
		generation: false,
		familyTreeName: false,
		membershipType: false,
		registeredAt: false,
		dateOfBirth: false, maritalStatus: false, bloodGroup: false,
		"address.city": false, "address.addressLine1": false,
		"address.state": false, "address.country": false,
		deceased: false, outsider: false, sideOfFamily: false, pageNumber: false,
		emailVerified: false, phoneNumberWhatsAppVerified: false,
	});
	const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 50});

	// Bulk Export
	const [isExporting, setIsExporting] = useState(false);

	// WhatsApp modal
	const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);
	const [waTemplateName, setWaTemplateName] = useState("");
	const [waLanguageCode, setWaLanguageCode] = useState("en_US");
	const [isSendingMsg, setIsSendingMsg] = useState(false);

	// Email modal
	const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
	const [emailSubject, setEmailSubject] = useState("");
	const [emailBody, setEmailBody] = useState("");
	const [isSendingEmail, setIsSendingEmail] = useState(false);

	const [familyTreeFilterOptions, setFamilyTreeFilterOptions] = useState([]);

	useEffect(() => {
		getDistinctFamilyTreeNamesApi(axiosPrivate)
			.then(res => {
				if (res.success) {
					setFamilyTreeFilterOptions([
						...BLANK_AWARE_OPTIONS,
						...res.data.map(n => ({label: n, value: n})),
					]);
				}
			})
			.catch(() => {
			});
	}, []);

	/**
	 * Converts the columnFilters object into the typed payload the backend expects.
	 * String "true"/"false" → boolean, numeric strings → number, rest stay as string.
	 */
	const buildFiltersPayload = () => {
		if (Object.keys(columnFilters).length === 0) return undefined;
		// Pass the arrays directly; backend interprets them
		return columnFilters;
	};

	const fetchUsers = async () => {
		setIsLoading(true);
		try {
			const payload = {
				page: pagination.pageIndex + 1,
				limit: pagination.pageSize,
				globalSearch: debouncedSearch,
				sortField: sorting.length ? sorting[0].id : "createdAt",
				sortOrder: sorting.length && sorting[0].desc ? "desc" : "asc",
				filters: buildFiltersPayload(),
				accountStatus: accountStatusFilter !== "all" ? accountStatusFilter : undefined,
			};
			const response = await getPaginatedUsers(axiosPrivate, payload);
			if (response.success) {
				setUsersData(response.data);
				setPageCount(response.pagination.totalPages);
				setTotalUsers(response.pagination.totalCount);
			} else {
				setUsersData([]);
			}
		} catch {
			toast.error("Failed to fetch member data.");
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		fetchUsers();
	}, [pagination.pageIndex, pagination.pageSize, sorting, debouncedSearch, columnFilters, accountStatusFilter]);


	// ── 3. FILTER HANDLERS ────────────────────────────────────────────────────

	/** All state resets are batched in one event-handler call → single re-render */
	const resetPageAndSelection = () => {
		setPagination(prev => ({...prev, pageIndex: 0}));
		setRowSelection({});
	};

	const handleAccountStatusChange = (status) => {
		setAccountStatusFilter(status);
		resetPageAndSelection();
	};

	const handleColumnFilterChange = (columnId, value) => {
		setColumnFilters(prev => {
			const next = {...prev};

			// undefined = explicit "clear this column entirely" (from chip X button)
			if (value === undefined) {
				delete next[columnId];
				return next;
			}

			// Otherwise it's an array from the popover Apply button
			if (Array.isArray(value) && value.length > 0) {
				next[columnId] = value;
			} else {
				delete next[columnId];
			}
			return next;
		});
		resetPageAndSelection();
	};

	const handleGlobalSearchChange = (value) => {
		setGlobalSearch(value);
		resetPageAndSelection();
	};

	const handleClearAllFilters = () => {
		setColumnFilters({});
		setGlobalSearch("");
		setAccountStatusFilter("all");
		resetPageAndSelection();
	};

	// Derived counts for display
	const activeFilterChips = Object.entries(columnFilters).map(([key, values]) => {
		const config = COLUMN_FILTER_CONFIGS[key];
		const labels = values.map(v => {
			const opt = config?.options.find(o => o.value === v);
			return opt?.label ?? v;
		});
		const display = labels.length > 2
			? `${labels.slice(0, 2).join(", ")} +${labels.length - 2}`
			: labels.join(", ");
		return {key, label: `${COLUMN_LABELS[key] ?? key}: ${display}`};
	});
	const totalActiveFilters = activeFilterChips.length + (globalSearch ? 1 : 0);


	// ── 4. COLUMN DEFINITIONS ─────────────────────────────────────────────────

	const columns = useMemo(() => [

		// ── Checkbox ──
		{
			id: "select",
			enableHiding: false,
			enableSorting: false,
			size: 40,
			header: ({table}) => (
				<input
					type="checkbox"
					className="h-4 w-4 rounded border-gray-300 text-[#D26A53] focus:ring-[#D26A53] cursor-pointer"
					checked={table.getIsAllRowsSelected()}
					onChange={table.getToggleAllRowsSelectedHandler()}
				/>
			),
			cell: ({row}) => (
				<input
					type="checkbox"
					className="h-4 w-4 rounded border-gray-300 text-[#D26A53] focus:ring-[#D26A53] cursor-pointer"
					checked={row.getIsSelected()}
					onChange={row.getToggleSelectedHandler()}
				/>
			),
		},

		// ── User ID (DB) ──
		{
			id: "_id",
			enableHiding: false,
			enableSorting: false,
			size: 70,   // reduced from 90
			header: "ID",
			cell: ({row}) => {
				const id = row.original._id ?? "";
				const shortId = id.slice(-6);  // 6 chars instead of 8
				return (
					<div className="flex items-center gap-1 group">
                <span
	                className="font-mono text-[10px] text-gray-400 tracking-tight"
	                title={id}
                >
                    …{shortId}
                </span>
						<button
							onClick={(e) => {
								e.stopPropagation();
								navigator.clipboard.writeText(id);
								toast.success("ID copied!", {autoClose: 1500});
							}}
							className="opacity-0 group-hover:opacity-100 transition-opacity
                        p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
							title="Copy full ID"
						>
							<ClipboardDocumentIcon className="h-3 w-3"/>
						</button>
					</div>
				);
			},
		},

		// ── Account Status ──
		// {
		// 	id: "accountStatus",
		// 	enableHiding: false,
		// 	enableSorting: false,
		// 	size: 130,
		// 	header: "Status",
		// 	cell: ({row}) => <StatusBadge status={getAccountStatus(row.original)}/>,
		// },

		// ── Member Name (fixed) ──
		{
			id: "fullName",
			enableHiding: false,
			accessorFn: row =>
				`${row.firstName ?? ""} ${row.middleName ? row.middleName + " " : ""}${row.lastName ?? ""}`.trim(),
			header: "Member Name",
			cell: info => {
				const user = info.row.original;
				const hasRealPhoto = user.imageUrl && user.imageUrl.length > 2;
				const initials = user.imageUrl?.length <= 2
					? user.imageUrl.toUpperCase()
					: user.firstName?.charAt(0)?.toUpperCase() ?? "?";

				return (
					<div className="flex items-center gap-3">
						<div
							className={`h-8 w-8 shrink-0 rounded-full flex items-center justify-center
                    text-white font-custom-mono-bold text-xs shadow-sm overflow-hidden
                    ${hasRealPhoto ? "" : "bg-[#D26A53]"}`}
							style={user.imageUrlBg && !hasRealPhoto
								? {backgroundColor: user.imageUrlBg.replace("bg-[", "").replace("]", "")}
								: {}}
						>
							{hasRealPhoto
								? <img src={user.imageUrl} alt={user.firstName} className="w-full h-full object-cover"
								       referrerPolicy="no-referrer"/>
								: initials
							}
						</div>
						<div className="flex flex-col min-w-0">
							<span className="font-custom-medium text-gray-900 truncate">{info.getValue()}</span>
							{user.membershipNumber && (
								<span className="text-[10px] text-gray-400">#{user.membershipNumber}</span>
							)}
						</div>
					</div>
				);
			}
		},

		// ── Phone ──
		{
			id: "phone.number",
			enableSorting: false,
			accessorFn: row =>
				row.phone?.number ? `${row.phone.dialingCode ?? ""} ${row.phone.number}` : null,
			header: "Phone",
			cell: info => info.getValue()
				? <span className="font-custom-regular text-gray-700">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Email ──
		{
			accessorKey: "email",
			header: "Email Address",
			cell: info => info.getValue()
				? <span
					className="font-custom-regular text-gray-700 truncate max-w-[200px] block">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Membership No. ──
		{
			accessorKey: "membershipNumber",
			header: "Membership No.",
			size: 130,
			cell: info => info.getValue()
				? <span className="font-custom-mono-bold text-gray-700">{info.getValue()}</span>
				: <span className="text-gray-300">N/A</span>,
		},

		// ── Is Member ──
		{
			accessorKey: "isMember",
			header: "Membership",
			size: 140,
			cell: info => info.getValue()
				? <span
					className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-custom-semi-bold bg-[#A20000]/8 text-[#A20000] border border-[#A20000]/20">Member</span>
				: <span
					className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-custom-semi-bold bg-gray-100 text-gray-500 border border-gray-200">Non-Member</span>,
		},

		// ── Gender (hidden by default) ──
		{
			accessorKey: "gender",
			header: "Gender",
			size: 90,
			cell: info => info.getValue()
				? <span className="font-custom-regular text-gray-700">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Membership Type (hidden by default) ──
		{
			accessorKey: "membershipType",
			header: "Membership Type",
			cell: info => info.getValue()
				? <span className="font-custom-regular text-gray-700">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Generation (hidden by default) ──
		{
			accessorKey: "generation",
			header: "Generation",
			size: 120,
			cell: info => info.getValue()
				? <span className="font-custom-mono-bold text-gray-600">Gen {info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Family Tree Name (hidden by default) ──
		{
			accessorKey: "familyTreeName",
			header: "Family Tree Name",
			cell: info => info.getValue()
				? <span
					className="font-custom-regular text-gray-700 truncate max-w-[160px] block">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Registered On (hidden by default) ──
		{
			accessorKey: "registeredAt",
			header: "Registered On",
			size: 130,
			cell: info => info.getValue()
				? <span className="font-custom-regular text-gray-600 text-xs">
                    {new Date(info.getValue()).toLocaleDateString("en-IN", {
	                    day: "2-digit",
	                    month: "short",
	                    year: "numeric"
                    })}
                  </span>
				: <span className="text-gray-300 text-xs">Not registered</span>,
		},

		// ── Date of Birth ──
		{
			accessorKey: "dateOfBirth",
			header: "Date of Birth",
			size: 120,
			cell: info => info.getValue()
				? <span className="text-xs text-gray-600">
            {new Date(info.getValue()).toLocaleDateString("en-IN", {day: "2-digit", month: "short", year: "numeric"})}
          </span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Marital Status ──
		{
			accessorKey: "maritalStatus",
			enableSorting: false,
			header: "Marital Status",
			size: 120,
			cell: info => info.getValue()
				? <span className="text-xs text-gray-700">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Blood Group ──
		{
			accessorKey: "bloodGroup",
			enableSorting: false,
			header: "Blood Group",
			size: 100,
			cell: info => info.getValue()
				? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
            font-custom-semi-bold bg-red-50 text-red-700 border border-red-200">
            {info.getValue()}
          </span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Address Line 1 ──
		{
			id: "address.addressLine1",
			accessorFn: row => row.address?.addressLine1,
			enableSorting: false,
			header: "Address Line 1",
			size: 200,
			cell: info => info.getValue()
				? <span className="text-xs text-gray-700 truncate block">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// ── City ──
		{
			id: "address.city",
			accessorFn: row => row.address?.city,
			enableSorting: false,
			header: "City",
			size: 130,
			cell: info => info.getValue()
				? <span className="text-xs text-gray-700 truncate block">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// ── State ──
		{
			id: "address.state",
			accessorFn: row => row.address?.state,
			enableSorting: false,
			header: "State",
			size: 130,
			cell: info => info.getValue()
				? <span className="text-xs text-gray-700 truncate block">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Country ──
		{
			id: "address.country",
			accessorFn: row => row.address?.country,
			enableSorting: false,
			header: "Country",
			size: 120,
			cell: info => info.getValue()
				? <span className="text-xs text-gray-700 truncate block">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Deceased ──
		{
			accessorKey: "deceased",
			enableSorting: false,
			header: "Deceased",
			size: 100,
			cell: info => info.getValue() === true
				? <span className="text-xs font-custom-semi-bold text-gray-500">Deceased</span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Outsider ──
		{
			accessorKey: "outsider",
			enableSorting: false,
			header: "Outsider",
			size: 90,
			cell: info => info.getValue() === true
				? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
            font-custom-semi-bold bg-purple-50 text-purple-700 border border-purple-200">
            Yes
          </span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Side of Family ──
		{
			accessorKey: "sideOfFamily",
			enableSorting: false,
			header: "Side of Family",
			size: 130,
			cell: info => info.getValue()
				? <span className="text-xs text-gray-700">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Page Number ──
		{
			accessorKey: "pageNumber",
			header: "Page No.",
			size: 90,
			cell: info => info.getValue()
				? <span className="font-custom-mono-bold text-gray-600 text-xs">{info.getValue()}</span>
				: <span className="text-gray-300">—</span>,
		},

		// ── Email Verified ──
		{
			accessorKey: "emailVerified",
			enableSorting: false,
			header: "Email Verified",
			size: 120,
			cell: info => info.getValue()
				?
				<span
					className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
                    font-custom-semi-bold bg-emerald-50 text-emerald-700 border border-emerald-200"
				>
                    Verified
                </span>
				:
				<span
					className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
                        font-custom-semi-bold bg-gray-100 text-gray-400 border border-gray-200"
				>
                    Not verified
                </span>
		},

		// ── WhatsApp Verified ──
		{
			accessorKey: "phoneNumberWhatsAppVerified",
			enableSorting: false,
			header: "WhatsApp Verified",
			size: 150,
			cell: info => info.getValue()
				?
				<span
					className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
                    font-custom-semi-bold bg-emerald-50 text-emerald-700 border border-emerald-200"
				>
		            Verified
				</span>
				:
				<span
					className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
                        font-custom-semi-bold bg-gray-100 text-gray-400 border border-gray-200"
				>
                    Not verified
                </span>
		},

		// ── Actions (fixed) ──
		{
			id: "actions",
			enableHiding: false,
			enableSorting: false,
			size: 60,
			header: () => <div className="text-right">Actions</div>,
			cell: ({row}) => (
				<div className="flex justify-end">
					<Menu as="div" className="relative inline-block text-left">
						<Menu.Button className="p-1 rounded-md hover:bg-gray-100 transition-colors">
							<EllipsisHorizontalIcon className="h-5 w-5 text-gray-500"/>
						</Menu.Button>
						<Transition
							as={Fragment}
							enter="transition ease-out duration-100"
							enterFrom="transform opacity-0 scale-95"
							enterTo="transform opacity-100 scale-100"
							leave="transition ease-in duration-75"
							leaveFrom="transform opacity-100 scale-100"
							leaveTo="transform opacity-0 scale-95"
						>
							<Menu.Items
								className="absolute right-0 mt-2 w-44 origin-top-right rounded-xl bg-white shadow-xl ring-1 ring-black/5 focus:outline-none z-10 p-1">
								<div className="flex flex-col gap-0.5">
									<Menu.Item>
										{({active}) => (
											<button
												onClick={() => {
													navigator.clipboard.writeText(row.original.email ?? "");
													toast.success("Email copied!");
												}}
												className={`${active ? "bg-gray-100" : ""} w-full flex items-center rounded-lg px-2.5 py-2 text-sm font-custom-medium text-gray-700`}
											>
												Copy Email
											</button>
										)}
									</Menu.Item>
									<Menu.Item>
										{({active}) => (
											<button
												onClick={() => {
													if (row.original.phone?.number) {
														navigator.clipboard.writeText(`${row.original.phone.dialingCode ?? ""}${row.original.phone.number}`);
														toast.success("Phone copied!");
													} else {
														toast.error("No phone number on record.");
													}
												}}
												className={`${active ? "bg-gray-100" : ""} w-full flex items-center rounded-lg px-2.5 py-2 text-sm font-custom-medium text-gray-700`}
											>
												Copy Phone
											</button>
										)}
									</Menu.Item>
									<div className="my-0.5 border-t border-gray-100"/>
									<Menu.Item>
										{({active}) => (
											<button
												onClick={() => toast.info("Profile view coming soon!")}
												className={`${active ? "bg-gray-100" : ""} w-full flex items-center rounded-lg px-2.5 py-2 text-sm font-custom-medium text-gray-700`}
											>
												View Profile
											</button>
										)}
									</Menu.Item>
								</div>
							</Menu.Items>
						</Transition>
					</Menu>
				</div>
			),
		},
	], []);


	// ── 5. TABLE INITIALIZATION ───────────────────────────────────────────────

	const table = useReactTable({
		data: usersData,
		columns,
		state: {sorting, rowSelection, pagination, columnVisibility},
		pageCount,
		manualPagination: true,
		manualSorting: true,
		onSortingChange: setSorting,
		onRowSelectionChange: setRowSelection,
		onPaginationChange: setPagination,
		onColumnVisibilityChange: setColumnVisibility,
		getCoreRowModel: getCoreRowModel(),
	});


	// ── 6. ACTION HANDLERS ────────────────────────────────────────────────────

	const handleBulkExport = async () => {
		setIsExporting(true);
		try {
			const selectedIds = table.getSelectedRowModel().rows.map(r => r.original._id);

			// Derive visible columns in their current display order
			// getVisibleLeafColumns() returns columns in order, respecting visibility
			const visibleColumns = table
				.getVisibleLeafColumns()
				.map(col => COLUMN_EXPORT_CONFIG[col.id])
				.filter(Boolean); // exclude select, _id, actions which have no export config

			// Always prepend name columns (firstName, middleName, lastName)
			// since fullName is a computed column — the backend needs raw fields
			const baseColumns = [
				{header: "First Name", key: "firstName"},
				{header: "Middle Name", key: "middleName"},
				{header: "Last Name", key: "lastName"},
			];

			const payload = {
				globalSearch: debouncedSearch || undefined,
				filters: buildFiltersPayload(),
				accountStatus: accountStatusFilter !== "all" ? accountStatusFilter : undefined,
				selectedIds: selectedIds.length > 0 ? selectedIds : null,
				sortField: sorting.length ? sorting[0].id : "createdAt",
				sortOrder: sorting.length && sorting[0].desc ? "desc" : "asc",
				// Pass the dynamic column list to the backend
				columns: [...baseColumns, ...visibleColumns],
			};

			const blob = await exportBulkUsers(axiosPrivate, payload);
			const url = window.URL.createObjectURL(new Blob([blob]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", `JoglekarKul_Members_${new Date().toISOString().split("T")[0]}.xlsx`);
			document.body.appendChild(link);
			link.click();
			link.parentNode.removeChild(link);
			toast.success("Export successful!");
		} catch {
			toast.error("Export failed. Please try again.");
		}
		setIsExporting(false);
	};

	const handleSendBulkWhatsApp = async () => {
		setIsSendingMsg(true);
		try {
			const selectedIds = table.getSelectedRowModel().rows.map(r => r.original._id);
			const response = await sendBulkWhatsAppApi(axiosPrivate, {
				userIds: selectedIds,
				templateName: waTemplateName,
				languageCode: waLanguageCode,
			});
			if (response.success) {
				toast.success(response.message);
				setIsWhatsAppModalOpen(false);
				setWaTemplateName("");
				setRowSelection({});
			} else {
				toast.error(response.message);
			}
		} catch {
			toast.error("An error occurred while sending messages.");
		} finally {
			setIsSendingMsg(false);
		}
	};

	const handleSendBulkEmail = async () => {
		setIsSendingEmail(true);
		try {
			const selectedIds = table.getSelectedRowModel().rows.map(r => r.original._id);
			const response = await sendBulkCommunicationApi(axiosPrivate, {
				userIds: selectedIds,
				channels: ["email"],
				subject: emailSubject,
				messageBody: emailBody,
			});
			if (response.success) {
				toast.success(response.message ?? "Emails dispatched successfully!");
				setIsEmailModalOpen(false);
				setEmailSubject("");
				setEmailBody("");
				setRowSelection({});
			} else {
				toast.error(response.message);
			}
		} catch {
			toast.error("An error occurred while sending emails.");
		} finally {
			setIsSendingEmail(false);
		}
	};


	// ── 7. DERIVED VALUES ─────────────────────────────────────────────────────

	const selectedRows = table.getSelectedRowModel().rows;
	const hasSelection = selectedRows.length > 0;
	const validPhoneCount = selectedRows.filter(r => r.original.phone?.number).length;
	const validEmailCount = selectedRows.filter(r => r.original.email).length;


	useEffect(() => {
		console.log(table.getAllLeafColumns().map(c => c.id + ' canHide:' + c.getCanHide()));
	}, []);

	// ─────────────────────────────────────────────────────────────────────────
	// RENDER
	// ─────────────────────────────────────────────────────────────────────────

	// <div className="flex flex-col h-full gap-3 p-4">

	return (
		<div className="flex flex-col h-full min-h-0 overflow-hidden gap-3 p-4">

			{/* ── Page Header (matches Analytics/Events pattern) ── */}
			<div className="flex items-center gap-2 shrink-0">
				<FlyoutSuperUserNavigationMenu
					selectedMenuOption={selectedMenuOption}
					setSelectedMenuOption={setSelectedMenuOption}
				/>
				<h1
					className="font-custom-bold text-gray-900
		                portrait:text-[1.25rem] portrait:jk:text-[1.5rem]
		                landscape:text-[0.875rem] landscape:jk:text-[1rem]"
				>
					Users
				</h1>
			</div>

			{/* ── Account Status Tabs ── */}
			<div className="flex items-end gap-0.5 border-b border-gray-100 overflow-x-auto no-scrollbar shrink-0">
				{ACCOUNT_STATUS_TABS.map((tab) => {
					const isActive = accountStatusFilter === tab.id;
					return (
						<button
							key={tab.id}
							onClick={() => handleAccountStatusChange(tab.id)}
							className={`px-3.5 py-2 text-sm font-custom-semi-bold rounded-t-lg whitespace-nowrap transition-all
                                ${isActive
								? "text-[#A20000] border-b-2 border-[#A20000] -mb-px bg-white"
								: "text-gray-500 hover:text-gray-800 hover:bg-gray-50"
							}`}
						>
							{tab.label}
							{isActive && totalUsers > 0 && (
								<span
									className="ml-1.5 text-[10px] font-custom-bold bg-[#A20000]/10 text-[#A20000] px-1.5 py-0.5 rounded-full">
                                    {totalUsers.toLocaleString("en-IN")}
                                </span>
							)}
						</button>
					);
				})}
			</div>

			{/* ── Toolbar ── */}
			<div className="flex items-center gap-2 shrink-0">

				{/* Search — grows to fill available space */}
				<div className="relative flex-1 min-w-0">
					<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400"/>
					<input
						type="text"
						placeholder="Search…"
						value={globalSearch}
						onChange={(e) => handleGlobalSearchChange(e.target.value)}
						className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-lg text-sm
			                focus:outline-none focus:ring-1 focus:ring-[#D26A53]
			                transition-all shadow-sm font-custom-medium"
					/>
					{globalSearch && (
						<button
							onClick={() => handleGlobalSearchChange("")}
							className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
						>
							<XMarkIcon className="h-3.5 w-3.5"/>
						</button>
					)}
				</div>

				{/* Action buttons — always in a single row, icon-only on mobile */}
				<div className="flex items-center gap-1.5 shrink-0">
					{hasSelection && (
						<span
							className="text-xs font-custom-semi-bold text-[#D26A53]
				                bg-[#D26A53]/10 px-2 py-1.5 rounded-lg border border-[#D26A53]/20
				                whitespace-nowrap hidden sm:inline-flex"
						>
			                {selectedRows.length} selected
			            </span>
					)}

					<button
						disabled={!hasSelection} onClick={() => setIsWhatsAppModalOpen(true)}
						className="p-2 border border-gray-200 rounded-lg text-sm shadow-sm
			                transition-colors disabled:opacity-40 disabled:cursor-not-allowed
			                hover:bg-gray-50 text-gray-700"
						title="WhatsApp selected"
					>
						<FaWhatsapp className="h-4 w-4 text-[#25D366]"/>
					</button>

					<button
						disabled={!hasSelection} onClick={() => setIsEmailModalOpen(true)}
						className="p-2 border border-gray-200 rounded-lg text-sm shadow-sm
			                transition-colors disabled:opacity-40 disabled:cursor-not-allowed
			                hover:bg-gray-50 text-gray-700"
						title="Email selected"
					>
						<EnvelopeIcon className="h-4 w-4"/>
					</button>

					<button
						onClick={handleBulkExport} disabled={isExporting}
						className="p-2 border border-gray-200 rounded-lg text-sm shadow-sm
                            transition-colors hover:bg-gray-50 text-gray-700"
						title="Export"
					>
						{isExporting
							? <ArrowPathIcon className="h-4 w-4 animate-spin"/>
							: <ArrowDownTrayIcon className="h-4 w-4"/>
						}
					</button>

					<Popover className="relative">
						<Popover.Button
							className="p-2 border border-gray-200 rounded-lg text-sm
                                shadow-sm hover:bg-gray-50 text-gray-700 outline-none"
							title="Toggle columns"
						>
							<AdjustmentsHorizontalIcon className="h-4 w-4"/>
						</Popover.Button>
						<Transition
							as={Fragment}
							enter="transition duration-100 ease-out"
							enterFrom="transform scale-95 opacity-0"
							enterTo="transform scale-100 opacity-100"
							leave="transition duration-75 ease-out"
							leaveFrom="transform scale-100 opacity-100"
							leaveTo="transform scale-95 opacity-0"
						>
							<Popover.Panel
								className="absolute right-0 z-10 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-xl p-2">
								<p className="px-2 py-1.5 text-[10px] font-custom-semi-bold text-gray-400 uppercase tracking-wider border-b border-gray-100 mb-1 sticky top-0 bg-white">
									Toggle Columns
								</p>
								<div className="flex flex-col gap-0.5 mt-1 max-h-64 overflow-y-auto thin-scrollbar">
									{table.getAllLeafColumns().map(col => {
										if (!col.getCanHide()) return null;
										return (
											<label
												key={col.id}
												className="flex items-center gap-2.5 cursor-pointer text-sm font-custom-medium text-gray-700 hover:bg-gray-50 px-2 py-1.5 rounded-lg"
											>
												<input
													type="checkbox"
													checked={col.getIsVisible()}
													onChange={col.getToggleVisibilityHandler()}
													className="rounded border-gray-300 text-[#D26A53] focus:ring-[#D26A53] h-3.5 w-3.5"
												/>
												{typeof col.columnDef.header === "string" ? col.columnDef.header : col.id}
											</label>
										);
									})}
								</div>
							</Popover.Panel>
						</Transition>
					</Popover>
				</div>
			</div>

			{/* ── Active Filter Chips ── */}
			{activeFilterChips.length > 0 && (
				<div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-custom-semi-bold text-gray-400 flex items-center gap-1 shrink-0">
                        <FunnelIcon className="h-3.5 w-3.5"/>
                        Active filters:
                    </span>
					{activeFilterChips.map(chip => (
						<span
							key={chip.key}
							className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-custom-semi-bold bg-[#D26A53]/10 text-[#D26A53] border border-[#D26A53]/20"
						>
                            {chip.label}
							<button
								onClick={() => handleColumnFilterChange(chip.key, undefined)}
								className="ml-0.5 hover:text-[#A20000] transition-colors"
							>
                                <XMarkIcon className="h-3 w-3"/>
                            </button>
                        </span>
					))}
					<button
						onClick={handleClearAllFilters}
						className="text-xs font-custom-semi-bold text-gray-400 hover:text-gray-700 underline underline-offset-2 transition-colors"
					>
						Clear all
					</button>
				</div>
			)}

			{/* ── Table ── */}
			<div
				className="flex-1 min-h-0 min-w-0 relative shadow-sm rounded-xl"
				style={{clipPath: "inset(0 round 0.75rem)"}}
			>
				{/* Absolute Border */}
				<div className="absolute inset-0 rounded-xl border border-gray-200 pointer-events-none z-10"/>

				{/* Loading overlay */}
				{isLoading && (
					<div
						className="absolute inset-0 bg-white/70 z-10 flex flex-col items-center justify-center gap-2 backdrop-blur-[1px]">
						<div className="animate-spin rounded-full h-7 w-7 border-b-2 border-[#A20000]"/>
						<p className="text-xs font-custom-medium text-gray-400">Fetching members…</p>
					</div>
				)}

				{/* Table */}
				<div className="absolute inset-0 overflow-auto thin-scrollbar rounded-xl bg-white">
					<table className="w-full min-w-[44rem] table-fixed text-left text-sm">
						<thead
							className="sticky top-0 z-[5] bg-gray-50/80 backdrop-blur-sm border-b border-gray-200">
						{table.getHeaderGroups().map(headerGroup => (
							<tr key={headerGroup.id}>
								{headerGroup.headers.map(header => {
									const canSort = header.column.getCanSort();
									const isSorted = header.column.getIsSorted();
									// Resolve the key used for COLUMN_FILTER_CONFIGS lookup
									const filterKey = header.column.columnDef.accessorKey ?? header.column.id;
									const hasFilter = !!COLUMN_FILTER_CONFIGS[filterKey] || filterKey === "familyTreeName";

									return (
										<th
											key={header.id}
											style={{width: header.getSize()}}
											className="h-10 px-3 text-[10px] font-custom-semi-bold text-gray-500 uppercase tracking-wider whitespace-nowrap align-middle"
										>
											{header.isPlaceholder ? null : (
												<div className="flex items-center gap-1">
													{/* Sort trigger */}
													<div
														className={`flex items-center gap-1 ${canSort ? "cursor-pointer select-none hover:text-gray-900 transition-colors" : ""}`}
														onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
													>
														{flexRender(header.column.columnDef.header, header.getContext())}
														{isSorted === "asc" &&
															<ChevronUpIcon className="h-3 w-3 text-[#D26A53]"/>}
														{isSorted === "desc" &&
															<ChevronDownIcon className="h-3 w-3 text-[#D26A53]"/>}
													</div>

													{/* Per-column filter */}
													{hasFilter && (
														<ColumnFilterPopover
															key={JSON.stringify(columnFilters[filterKey] ?? [])}
															columnId={filterKey}
															currentValue={columnFilters[filterKey]}
															onFilterChange={handleColumnFilterChange}
															configOptions={filterKey === "familyTreeName"
																? (familyTreeFilterOptions.length > 0 ? {options: familyTreeFilterOptions} : null)
																: COLUMN_FILTER_CONFIGS[filterKey]
															}
														/>
													)}
												</div>
											)}
										</th>
									);
								})}
							</tr>
						))}
						</thead>

						<tbody className="divide-y divide-gray-50">
						{table.getRowModel().rows.length > 0 ? (
							table.getRowModel().rows.map(row => (
								<tr
									key={row.id}
									className={`transition-colors ${row.getIsSelected() ? "bg-[#D26A53]/5" : "hover:bg-gray-50/60"}`}
								>
									{row.getVisibleCells().map(cell => (
										<td
											key={cell.id}
											className="px-3 py-2.5 text-gray-800 whitespace-nowrap font-custom-regular"
											style={{width: cell.column.getSize()}}
										>
											{flexRender(cell.column.columnDef.cell, cell.getContext())}
										</td>
									))}
								</tr>
							))
						) : (
							!isLoading && (
								<tr>
									<td colSpan={columns.length} className="h-48 text-center">
										<div className="flex flex-col items-center gap-2 text-gray-400">
											<MagnifyingGlassIcon className="h-10 w-10 opacity-20"/>
											<p className="font-custom-medium text-sm">No members found</p>
											<p className="text-xs">Try adjusting your search or removing filters</p>
											{totalActiveFilters > 0 && (
												<button
													onClick={handleClearAllFilters}
													className="mt-1 text-xs font-custom-semi-bold text-[#D26A53] hover:underline"
												>
													Clear all filters
												</button>
											)}
										</div>
									</td>
								</tr>
							)
						)}
						</tbody>
					</table>
				</div>
			</div>

			{/* ── Pagination ── */}
			<div className="flex items-center justify-between gap-2 pt-0.5 shrink-0">

				{/* Left: total count */}
				<div className="text-xs text-gray-500 font-custom-medium shrink-0">
					{hasSelection
						? <span className="text-[#D26A53] font-custom-semi-bold">
                {selectedRows.length} of {totalUsers.toLocaleString("en-IN")}
              </span>
						: <span>{totalUsers.toLocaleString("en-IN")} members</span>
					}
				</div>

				{/* Right: page controls */}
				<div className="flex items-center gap-2">

					{/* Rows per page — hidden on mobile */}
					<div className="hidden sm:flex items-center gap-2">
						<p className="text-xs font-custom-medium text-gray-600">Rows</p>
						<select
							value={table.getState().pagination.pageSize}
							onChange={e => table.setPageSize(Number(e.target.value))}
							className="border border-gray-200 rounded-lg px-2 py-1 text-xs
                    bg-white font-custom-medium outline-none
                    focus:ring-1 focus:ring-[#D26A53] text-gray-700"
						>
							{[25, 50, 100, 200].map(s => <option key={s} value={s}>{s}</option>)}
						</select>
					</div>

					{/* Page indicator */}
					<div className="text-xs font-custom-medium text-gray-600 whitespace-nowrap">
						{table.getState().pagination.pageIndex + 1} / {table.getPageCount() || 1}
					</div>

					{/* Navigation */}
					<div className="flex items-center gap-1">
						<button onClick={() => table.setPageIndex(0)}
						        disabled={!table.getCanPreviousPage()}
						        className="p-1.5 border border-gray-200 rounded-lg
                    disabled:opacity-40 disabled:cursor-not-allowed
                    hover:bg-gray-50 transition-colors hidden sm:block">
							<ChevronDoubleLeftIcon className="h-3.5 w-3.5 text-gray-600"/>
						</button>
						<button onClick={() => table.previousPage()}
						        disabled={!table.getCanPreviousPage()}
						        className="p-1.5 border border-gray-200 rounded-lg
                    disabled:opacity-40 disabled:cursor-not-allowed
                    hover:bg-gray-50 transition-colors">
							<ChevronLeftIcon className="h-3.5 w-3.5 text-gray-600"/>
						</button>
						<button onClick={() => table.nextPage()}
						        disabled={!table.getCanNextPage()}
						        className="p-1.5 border border-gray-200 rounded-lg
                    disabled:opacity-40 disabled:cursor-not-allowed
                    hover:bg-gray-50 transition-colors">
							<ChevronRightIcon className="h-3.5 w-3.5 text-gray-600"/>
						</button>
						<button onClick={() => table.setPageIndex(table.getPageCount() - 1)}
						        disabled={!table.getCanNextPage()}
						        className="p-1.5 border border-gray-200 rounded-lg
                    disabled:opacity-40 disabled:cursor-not-allowed
                    hover:bg-gray-50 transition-colors hidden sm:block">
							<ChevronDoubleRightIcon className="h-3.5 w-3.5 text-gray-600"/>
						</button>
					</div>
				</div>
			</div>

			{/* ══ WHATSAPP MODAL ══════════════════════════════════════════════ */}
			<Dialog
				open={isWhatsAppModalOpen}
				onClose={() => !isSendingMsg && setIsWhatsAppModalOpen(false)}
				className="relative z-[100]"
			>
				<div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true"/>
				<div className="fixed inset-0 flex items-center justify-center p-4">
					<Dialog.Panel
						className="mx-auto max-w-md w-full rounded-2xl bg-white p-6 shadow-2xl border border-gray-100">
						<Dialog.Title className="text-base font-custom-bold text-gray-900 mb-1 flex items-center gap-2">
							<ChatBubbleLeftEllipsisIcon className="h-5 w-5 text-[#D26A53]"/>
							Broadcast WhatsApp Message
						</Dialog.Title>
						<p className="text-sm text-gray-500 mb-4 font-custom-medium">
							Send a Meta-approved template to the selected members.
						</p>

						<div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-3 items-start mb-5">
							<ExclamationTriangleIcon className="h-5 w-5 text-amber-600 shrink-0 mt-0.5"/>
							<div className="text-sm text-amber-800 font-custom-medium space-y-0.5">
								<p><strong>{selectedRows.length}</strong> row(s) selected.</p>
								<p>Only <strong>{validPhoneCount}</strong> have a valid phone number. Messages will
									dispatch to those {validPhoneCount} users only.</p>
							</div>
						</div>

						<div className="flex flex-col gap-4 mb-5">
							<div>
								<label className="block text-sm font-custom-semi-bold text-gray-700 mb-1">
									Meta Template Name
								</label>
								<input
									type="text"
									placeholder="e.g., joglekarkul_event_invite"
									value={waTemplateName}
									onChange={(e) => setWaTemplateName(e.target.value)}
									className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-[#D26A53] outline-none text-sm font-custom-medium"
								/>
							</div>
							<div>
								<label className="block text-sm font-custom-semi-bold text-gray-700 mb-1">
									Language Code
								</label>
								<select
									value={waLanguageCode}
									onChange={(e) => setWaLanguageCode(e.target.value)}
									className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-[#D26A53] outline-none text-sm bg-white font-custom-medium"
								>
									<option value="en_US">English (en_US)</option>
									<option value="mr">Marathi (mr)</option>
								</select>
							</div>
						</div>

						<div className="flex justify-end gap-2">
							<button
								onClick={() => setIsWhatsAppModalOpen(false)}
								disabled={isSendingMsg}
								className="px-4 py-2 text-sm font-custom-semi-bold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl shadow-sm transition-colors"
							>
								Cancel
							</button>
							<button
								onClick={handleSendBulkWhatsApp}
								disabled={!waTemplateName || isSendingMsg || validPhoneCount === 0}
								className="px-4 py-2 text-sm font-custom-semi-bold text-white bg-[#D26A53] hover:bg-[#b85b46] rounded-xl shadow-sm disabled:opacity-50 transition-colors"
							>
								{isSendingMsg ? "Dispatching…" : `Send to ${validPhoneCount} User(s)`}
							</button>
						</div>
					</Dialog.Panel>
				</div>
			</Dialog>


			{/* ══ EMAIL MODAL ═════════════════════════════════════════════════ */}
			<Dialog
				open={isEmailModalOpen}
				onClose={() => !isSendingEmail && setIsEmailModalOpen(false)}
				className="relative z-[100]"
			>
				<div className="fixed inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true"/>
				<div className="fixed inset-0 flex items-center justify-center p-4">
					<Dialog.Panel
						className="mx-auto max-w-lg w-full rounded-2xl bg-white p-6 shadow-2xl border border-gray-100">
						<Dialog.Title className="text-base font-custom-bold text-gray-900 mb-1 flex items-center gap-2">
							<EnvelopeIcon className="h-5 w-5 text-[#D26A53]"/>
							Broadcast Email
						</Dialog.Title>
						<p className="text-sm text-gray-500 mb-4 font-custom-medium">
							Compose and send an email to all selected members.
						</p>

						<div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-3 items-start mb-5">
							<ExclamationTriangleIcon className="h-5 w-5 text-amber-600 shrink-0 mt-0.5"/>
							<div className="text-sm text-amber-800 font-custom-medium space-y-0.5">
								<p><strong>{selectedRows.length}</strong> row(s) selected.</p>
								<p>Only <strong>{validEmailCount}</strong> have a valid email address. Emails will
									dispatch to those {validEmailCount} users only.</p>
							</div>
						</div>

						<div className="flex flex-col gap-4 mb-5">
							<div>
								<label className="block text-sm font-custom-semi-bold text-gray-700 mb-1">
									Subject
								</label>
								<input
									type="text"
									placeholder="e.g., JoglekarKul Annual Gathering 2025"
									value={emailSubject}
									onChange={(e) => setEmailSubject(e.target.value)}
									className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-[#D26A53] outline-none text-sm font-custom-medium"
								/>
							</div>
							<div>
								<label
									className="block text-sm font-custom-semi-bold text-gray-700 mb-1 flex items-baseline gap-1.5">
									Message Body
									<span className="text-xs font-custom-regular text-gray-400">(HTML accepted)</span>
								</label>
								<textarea
									rows={8}
									placeholder="Write your message here…&#10;&#10;You can use HTML tags for formatting."
									value={emailBody}
									onChange={(e) => setEmailBody(e.target.value)}
									className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-[#D26A53] outline-none text-sm font-custom-medium resize-none"
								/>
							</div>
						</div>

						<div className="flex justify-end gap-2">
							<button
								onClick={() => setIsEmailModalOpen(false)}
								disabled={isSendingEmail}
								className="px-4 py-2 text-sm font-custom-semi-bold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-xl shadow-sm transition-colors"
							>
								Cancel
							</button>
							<button
								onClick={handleSendBulkEmail}
								disabled={!emailSubject || !emailBody || isSendingEmail || validEmailCount === 0}
								className="px-4 py-2 text-sm font-custom-semi-bold text-white bg-[#D26A53] hover:bg-[#b85b46] rounded-xl shadow-sm disabled:opacity-50 transition-colors"
							>
								{isSendingEmail ? "Sending…" : `Send to ${validEmailCount} User(s)`}
							</button>
						</div>
					</Dialog.Panel>
				</div>
			</Dialog>

		</div>
	);
};